import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from '@/components/Loader';
import EditableTable from "./JoinSourceTable";
import Modal from "@/components/Modal";
import ChangeLog from "@/components/ChangeLog";
import api from "@/auth/api";
import { alertSuccess, alertError } from '@/components/Alert';
import { fetchJointSourceList, setPageSize, setSearch } from "@/slices/jointSourceSlice/JointSourceSlice";
import SimplePagination from "@/components/SimplePagination";
import PanelUserSearch from "@/components/PanelUserSearch";
import PageSizeSelector from "@/components/PageSizeSelector";

export default function JoinList() {
  const dispatch = useDispatch();

  const { list, currentPage, totalPages, status, search, pageSize } = useSelector(
    (state) => state.sourceList
  );

  const [editRowId, setEditRowId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentLog, setCurrentLog] = useState([]);
  const [changeLogData, setChangeLogData] = useState([]); // optional, if you get from API
    const [searchInput, setSearchInput] = useState(search || "");

  const editableFields = ['title', 'status'];

  // ✅ fetch data on mount
  useEffect(() => {
    dispatch(fetchJointSourceList({ page: 1, search }));
  }, [dispatch]);

  // ✅ handle page size change
  const handlePageSizeChange = (size) => {
    dispatch(setPageSize(size));
    dispatch(fetchJointSourceList({ page: 1, search }));
  };

 
  // ✅ edit row
  const handleEdit = (rowId) => {
    setEditRowId(rowId);
    const rowData = list.find((item) => item.id === rowId);
    setEditedData(rowData);
  };

  const handleSave = async () => {
    try {
      const finalData = { id: editedData.id, title: editedData.title };
      await api.patch(`/account/panel/join`, finalData);
      alertSuccess("Data updated successfully");
      setEditRowId(null);
      dispatch(fetchJointSourceList({ page: currentPage, search }));
    } catch (error) {
      alertError(error?.response?.data?.errors);
      console.error("Error updating data:", error);
    }
  };

  const handleCancel = () => {
    setEditRowId(null);
    setEditedData({});
  };

  const handleLog = (id) => {
    const logData = changeLogData.find(f => f.id === id)?.oldTrack || [];
    setCurrentLog(logData);
    setIsModalOpen(true);
  };
  

    useEffect(() => {
    setSearchInput(search || "");
  }, [search]);
  

   // ✅ Debounced search (MAIN CONTROL)
    useEffect(() => {
      const timer = setTimeout(() => {
        dispatch(fetchJointSourceList({ page: 1, search: searchInput }));
      }, 800);
  
      return () => clearTimeout(timer);
    }, [searchInput]);

  const headers = list.length > 0 ? Object.keys(list[0]).filter(key => key !== 'id') : [];
  const hasData = list.length > 0;
  const isSearching = searchInput && searchInput.trim() !== "";



  if (status === "loading") {
    return (
      <div className="w-full pt-20 flex justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center p-6 pt-2">
      <PanelUserSearch value={searchInput} onChange={setSearchInput} />

      <div className="w-full flex items-center flex-col">
        <div className="max-w-[95%]">
          {hasData ? (
            <EditableTable
              headers={headers}
              rows={list}
              editRowId={editRowId}
              editedData={editedData}
              handleEdit={handleEdit}
              handleSave={handleSave}
              handleCancel={handleCancel}
              editableFields={editableFields}
              handleChange={(e, field, val) => {
                setEditedData(prev => ({
                  ...prev,
                  [field]: field === "status" ? !val : e.target.value
                }));
              }}
              handleLog={handleLog}
              editable={false}
            />
          ) : (
            <div className="w-full flex flex-col items-center justify-center py-20 text-gray-500">
              <div className="text-5xl mb-3">🔍</div>
              {searchInput ? (
                <>
                  <p className="text-lg font-medium">No results found</p>
                  <p className="text-sm mt-1">
                    for "<span className="font-semibold">{searchInput}</span>"
                  </p>
                </>
              ) : (
                <>
                  <p className="text-lg font-medium">No Data Available</p>
                  <p className="text-sm mt-1">There are no users to display</p>
                </>
              )}
            </div>
          )}
        </div>
      
       {!(isSearching && !hasData) && (
        <div className="flex mt-4 items-center justify-between w-11/12">
          
            <PageSizeSelector pageSize={pageSize} setPageSize={handlePageSizeChange} />
         
            <SimplePagination
              currentPage={currentPage}
              setCurrentPage={(page) => dispatch(fetchJointSourceList({ page, search }))}
              totalPages={totalPages}
            />
          
        </div>
       )}

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Change Log"
          content={<ChangeLog data={currentLog} />}
        />
      </div>
    </div>
  );
}